import { useState, useEffect, useRef } from "react";
import sal from "sal.js";
import "intl-tel-input/build/css/intlTelInput.css";
import { lazy, Suspense } from "react";
const ReCAPTCHA = lazy(() => import("react-google-recaptcha"));
import { Helmet } from "@dr.pogodin/react-helmet";


function Contact() {

   useEffect(() => {
    document.title = "Contact Us| Get in Touch With Our Experienced Team";
    // ADD class when Contact page loads
    document.body.classList.add("contact-page");
    sal();
    // REMOVE class when leaving page
    return () => {
      document.body.classList.remove("contact-page");
    };
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    countryCode: "+91",
    contactNumber: "",
    message: "",
    captchaToken: "",
  });

  const [errors, setErrors] = useState({});
  const [utilsLoaded, setUtilsLoaded] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const phoneInputRef = useRef(null);
  const itiRef = useRef(null);
  const recaptchaRef = useRef(null);

  const sanitizeInput = (str) =>
    str
      .replace(/</g, "")
      .replace(/>/g, "")
      .replace(/[\$]/g, "")
      .replace(/[{}]/g, "")
      .replace(/["']/g, "")
      .replace(/;/g, "")
      .replace(/\\/g, "")
      .trim();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!phoneInputRef.current) return;

    const inputEl = phoneInputRef.current;

    import("intl-tel-input").then(({ default: intlTelInput }) => {
    itiRef.current = intlTelInput(inputEl, {
      initialCountry: "in",
      nationalMode: false,
      formatOnDisplay: true,
      separateDialCode: true,
      utilsScript:
        "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.12/js/utils.js",
    });
    });

    const checkUtilsLoaded = setInterval(() => {
      if (
        window.intlTelInputUtils &&
        itiRef.current &&
        typeof itiRef.current.isValidNumber === "function"
      ) {
        setUtilsLoaded(true);
        clearInterval(checkUtilsLoaded);
      }
    }, 300);

    const handleCountryChange = () => {
      const selected = itiRef.current.getSelectedCountryData();
      setFormData((prev) => ({
        ...prev,
        countryCode: `+${selected.dialCode}`,
      }));
    };

    const handlePhoneInput = () => {
      const selected = itiRef.current.getSelectedCountryData();
      const number = itiRef.current.getNumber();
      setFormData((prev) => ({
        ...prev,
        contactNumber: number,
        countryCode: `+${selected.dialCode}`,
      }));
      setErrors((prev) => ({ ...prev, contactNumber: "" }));
    };

    inputEl.addEventListener("countrychange", handleCountryChange);
    inputEl.addEventListener("input", handlePhoneInput);

    return () => {
      inputEl.removeEventListener("countrychange", handleCountryChange);
      inputEl.removeEventListener("input", handlePhoneInput);
      clearInterval(checkUtilsLoaded);
      itiRef.current?.destroy();
    };
  }, []);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "contactNumber") return;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleNumericInput = (e) => {
    const original = e.target.value;
    const digitsOnly = original.replace(/\D/g, "");
    if (original !== digitsOnly) e.target.value = digitsOnly;
    setFormData((prev) => ({ ...prev, contactNumber: digitsOnly }));
    setErrors((prev) => ({ ...prev, contactNumber: "" }));
  };

  const validate = (data, phoneValue, isValid) => {
    const newErrors = {};

    if (!data.name.trim()) newErrors.name = "Full name is required.";

    if (!data.email.trim()) {
      newErrors.email = "Email address is required.";
    } else if (
      !/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(data.email)
    ) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!phoneValue.trim()) {
      newErrors.contactNumber = "Contact number is required.";
    } else if (!isValid) {
      newErrors.contactNumber = "Please enter a valid phone number.";
    }

    if (!data.captchaToken) {
      newErrors.captcha = "Please verify that you are not a robot.";
    }

    const suspiciousPattern =
      /\b(select|insert|delete|update|drop|truncate|script|iframe|alert|eval|union|--|#|;)\b/i;
    if (!data.message.trim()) {
      newErrors.message = "Message cannot be empty.";
    } else if (suspiciousPattern.test(data.message)) {
      newErrors.message = "Suspicious content detected in your message.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage("");
    setStatusType("");
    setIsSubmitting(true);

    if (!utilsLoaded || !window.intlTelInputUtils) {
      setStatusMessage("Please wait for phone validation to load fully.");
      setStatusType("error");
      setIsSubmitting(false);
      return;
    }

    let isValid = false;
    let validatedNumber = "";
    let countryCode = formData.countryCode;
    let phoneValue = phoneInputRef.current?.value?.trim() || "";
    let localNumber = phoneValue;

    if (itiRef.current) {
      const selected = itiRef.current.getSelectedCountryData();
      const dialCode = selected?.dialCode ? `+${selected.dialCode}` : "";
      const fullNumber = phoneValue.startsWith("+")
        ? phoneValue
        : `${dialCode}${phoneValue}`;

      try {
        validatedNumber = itiRef.current.getNumber(
          window.intlTelInputUtils.numberFormat.E164
        );
        if (!validatedNumber) validatedNumber = fullNumber;

        isValid = window.intlTelInputUtils.isValidNumber(
          validatedNumber,
          selected.iso2
        );

        if (selected.iso2 === "in") {
          const local = phoneValue.replace(/\D/g, "");
          if (local.length !== 10 || !/^[6-9]\d{9}$/.test(local)) {
            isValid = false;
            console.warn(
              "Invalid Indian number — must be 10 digits and start with 6-9"
            );
          }
        }

        countryCode = dialCode;
        localNumber = validatedNumber.replace(dialCode, "").trim();
      } catch (err) {
        console.error("Validation error:", err);
        validatedNumber = fullNumber;
      }
    }

    if (!validate(formData, phoneValue, isValid)) {
        setIsSubmitting(false);
        return;
      }

    const safeData = {
      conname: sanitizeInput(formData.name),
      conemail: sanitizeInput(formData.email),
      countryCode,
      contactNumber: sanitizeInput(localNumber),
      message: sanitizeInput(formData.message),
      captchaToken: formData.captchaToken,
    };

    try {
      const response = await fetch(
        `${import.meta.env.VITE_BACKEND_API_URL}/submit-contact`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(safeData),
        }
      );

      if (response.ok) {
        setStatusMessage("Message sent successfully!");
        setStatusType("success");
        setFormData({
          name: "",
          email: "",
          countryCode: "+91",
          contactNumber: "",
          message: "",
        });
        setErrors({});
        itiRef.current?.setNumber("");

        if (recaptchaRef.current) {
          recaptchaRef.current.reset();
        }

        setTimeout(() => {
          setStatusMessage("");
          setStatusType("");
        }, 3000);

      } else {
        setStatusMessage("Failed to send message. Please try again later.");
        setStatusType("error");
      }
    } catch (error) {
      console.error("Network error:", error);
      setStatusMessage("Error occurred. Please check your connection.");
      setStatusType("error");
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <>
    <Helmet>
        <title>Contact Us| Get in Touch With Our Experienced Team</title>
        <meta name="description" content="Infinitive Host is available 24/7 to resolve your overall queries. For better assistance, contact our team directly through chat, phone, email, or help desk." />
        <meta name="keywords" content="contact us, get in touch, customer support, reach out, contact information, business inquiries, customer service, contact form, support center, connect with us" />
        <meta property="og:url" content={`${import.meta.env.VITE_FRONTEND_URL}/contact-us`} />
        <link rel="canonical" href={`${import.meta.env.VITE_FRONTEND_WWW_URL}/contact-us`} />
        <meta property="og:title" content="Contact Us| Get in Touch With Our Experienced Team" />
        <meta property="og:description" content="Infinitive Host is available 24/7 to resolve your overall queries. For better assistance, contact our team directly through chat, phone, email, or help desk." />
    </Helmet>
    <section className="banner-bg bg-dark ssl-banner">
      <div className="container">
        <div className="row align-items-center g-4">
            <div className="col-xl-6">
                <div className="pb-40">
                <span className="fs-18 fw-bold text-primary mb-2 sal-animate" data-sal="slide-up" data-sal-duration="1000" data-sal-delay="300" data-sal-easing="ease-in-out-sine">NY Power Hub</span>
                <h1 className="text-white mb-3 sal-animate" data-sal="slide-up" data-sal-duration="500" data-sal-delay="300" data-sal-easing="ease-in-out-sine">Contact Us</h1>
                <p className="text-white mb-8 max-text-60 text-opacity-75 sal-animate" data-sal="slide-up" data-sal-duration="500" data-sal-delay="300" data-sal-easing="ease-in-out-sine">Your content isn’t basic, and even your stream isn’t basic-so why choose a basic server? With a streaming server Montreal, every single second hits differently. No buffering. No delays. Just pure streaming power. Let your live stream leave a mark in users' minds the way it’s meant to-smooth, fearless, and full of vibe.
                </p>
                </div>
            </div>
            <div className="col-lg-6 col-xxl-6"><div className="cloud-hero__img sal-animate" data-sal="fade" data-sal-duration="1000" data-sal-delay="300" data-sal-easing="ease-in-out-sine"><img alt="image" className="img-fluid" src="/images/partner-1.png"/></div></div>
        </div>
      </div>
    </section>
    <section className="features__area-three pb-40">
            <div className="container">
                <div className="features__inner-wrap-two">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section__title text-center mb-50">
                                <small className="d-inline-block mb-2 fw-bold bg-success-subtle text-success-emphasis border border-success-subtle px-3 py-1 rounded"><span className="d-inline-block w-2 h-2 bg-success rounded-circle me-1"></span>Cloud Hosting</small>
                                <h2 className="title">The Right Hosting Plan for Every Need</h2>
                                <p>Switch to a better host today! Migrate your website effortlessly with our expert support. Get started now!</p>
                            </div>
                        </div>
                    </div>
                    <div className="row gutter-y-24 mt-5">
                        <div className="col-md-6 mb-md-0 mb-5">
                            <div className="features__item-five">
                                <div className="features__content-five">
                                  <div className="text-center mt-2 mb-2 info-icon">
                                    <img src="images/support1.png" className="img-fluid" alt="" />
                                  </div>
                                    <h3 className="title">Support</h3>
                                    <p>Get fast, secure, and hassle-free WordPress hosting with automatic updates, daily backups, and expert support.</p>
                                    <p className="info-detail"><i className="fa fa-envelope text-muted inner-icon"></i> <a href="mailto:Support@infinitivehost.in" className="dark-font">Support@infinitivehost.in</a></p>
                                    <p className="info-detail"><i className="fa fa-mobile text-muted inner-icon"></i> <a href="tel:+918000469015" className="dark-font">+91-8000469015</a></p>
                                    <p className="info-detail"><i className="fa fa-mobile text-muted inner-icon"></i> US Toll-Free:&nbsp; <a href="tel:8885443118" className="dark-font">(888) 544-3118</a></p>
                                </div>
                                <div className="features__shape-three">
                                    <img src="images/features_shape_icon01.svg" alt="shape" />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="features__item-five features__item-six">
                                <div className="features__content-five">
                                    <div className="text-center mt-2 mb-2 info-icon">
                                      <img src="images/customization.png" className="img-fluid" alt="" />
                                    </div>
                                    <h3 className="title">Sales</h3>
                                    <p>Get fast, secure, and hassle-free WordPress hosting with automatic updates, daily backups, and expert support.</p>
                                    <p className="info-detail"><i className="fa fa-envelope text-muted inner-icon"></i> <a href="mailto:sales@infinitivehost.in" className="dark-font">sales@infinitivehost.in</a></p>
                                    <p className="info-detail"><i className="fa fa-mobile text-muted inner-icon"></i> <a href="tel:7737300013" className="dark-font">+91-7737300013</a></p>
                                    <p className="info-detail dark-font"> <i className="fa fa-clock text-muted inner-icon"></i> (9 AM - 12 AM IST, Mon–Sat)</p>
                                </div>
                                <div className="features__shape-three">
                                    <img src="images/features_shape_icon02.svg" alt="shape" />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 mt-5">
                            <div className="features__item-five">
                                <div className="features__content-five">
                                    <div className="text-center mt-2 mb-2 info-icon">
                                      <img src="images/technical.png" className="img-fluid" alt="" />
                                    </div>
                                    <h3 className="title">Billing</h3>
                                    <p>Get fast, secure, and hassle-free WordPress hosting with automatic updates, daily backups, and expert support.</p>
                                    <p className="info-detail"><i className="fa fa-envelope text-muted inner-icon"></i> <a href="mailto:billing@infinitivehost.in" className="dark-font">billing@infinitivehost.in</a></p>
                                    <p className="info-detail dark-font"><i className="fa fa-clock text-muted inner-icon"></i> (10 AM - 7 PM IST, Mon–Sat)</p>
                                </div>
                                <div className="features__shape-three">
                                    <img src="images/features_shape_icon01.svg" alt="shape" />
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6 mt-5">
                          <div className="features__item-five features__item-six">
                            <div className="features__content-five">
                              <div className="text-center mt-2 mb-2 info-icon">
                                <img src="images/account.png" className="img-fluid" alt="" />
                              </div>
                              <h3 className="title">HR</h3>
                              <p>Get fast, secure, and hassle-free WordPress hosting with automatic updates, daily backups, and expert support.</p>
                              <p className="info-detail"><i className="fa fa-envelope text-muted inner-icon"></i> <a href="mailto:hr@infinitivehost.in" className="dark-font">hr@infinitivehost.in</a></p>
                              <p className="info-detail dark-font"><i className="fa fa-clock text-muted inner-icon"></i> (10 AM - 7 PM IST, Mon–Sat)</p>
                            </div>
                            <div className="features__shape-three">
                              <img src="images/features_shape_icon02.svg" alt="shape" />
                            </div>
                          </div>
                        </div>

                         <div className="col-md-6 mt-5">
                          <div className="features__item-five features__item-six">
                            <div className="features__content-five">
                              <div className="text-center mt-2 mb-2 info-icon">
                                <img src="images/address.png" className="img-fluid" style={{ width: "44px" }} alt="" />
                              </div>
                              <h3 className="title">Office Address: Jaipur</h3>
                              <p className="info-detail dark-font">Office No: 201-202, Second Floor, Ajmer Rd, DCM, Jaipur, Rajasthan 302021</p>
                            </div>
                            <div className="features__shape-three">
                              <img src="images/features_shape_icon02.svg" alt="shape" />
                            </div>
                          </div>
                        </div>

                         <div className="col-md-6 mt-5">
                          <div className="features__item-five features__item-six">
                            <div className="features__content-five">
                              <div className="text-center mt-2 mb-2 info-icon">
                                <img src="images/address.png" className="img-fluid" style={{ width: "44px" }} alt="" />
                              </div>
                              <h3 className="title">Office Address: Dehradun</h3>
                              <p className="info-detail dark-font">Office no: 417 HQ, 4th Floor, DDPM, Boulevard,  Haridwar Bypass Road, Ajabpur Kalan, Shanti Vihar , Dehradun, 248001</p>
                            </div>
                            <div className="features__shape-three">
                              <img src="images/features_shape_icon02.svg" alt="shape" />
                            </div>
                          </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    <section className="pt-4 pb-32 position-relative z-1 overflow-hidden">
      <div className="container banner-wrapper">

        <div className="row pt-20">
          <div className="col-lg-6 text-center mb-lg-0 mb-5">
            <h2>Start Your Hosting Journey With Us</h2>
            <img src="images/contact.png" alt="Contact" className="img-fluid mt-3" />
          </div>

          <div className="col-lg-6 mt-lg-0 mt-5">
            <div className="card p-4 border border-1 border-dark rounded-3 dark-form">
              <div className="card-body">
                <h4 className="mb-5 form-main-title">Get In Touch</h4>

                {statusMessage && (
                  <div
                    className={`alert ${
                      statusType === "success" ? "alert-success" : "alert-danger"
                    }`}
                  >
                    {statusMessage}
                  </div>
                )}

                <form className="contact-form" onSubmit={handleSubmit}>
                  <div className="row mb-4">
                    <div className="col-lg-6">
                      <input className="form-control" type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Full name" />
                      {errors.name && <div className="error-msg mt-1">{errors.name}</div>}
                    </div>
                    <div className="col-lg-6 mt-lg-0 mt-4">
                      <input className="form-control" type="text" name="email" value={formData.email} onChange={handleInputChange} placeholder="Email Address" />
                      {errors.email && <div className="error-msg mt-1">{errors.email}</div>}
                    </div>
                  </div>

                  <div className="mb-4">
                    <input ref={phoneInputRef} className="form-control" type="tel" id="contactNumber" name="contactNumber" placeholder="Phone Number" onInput={handleNumericInput} inputMode="numeric" pattern="[0-9]*" />
                    {errors.contactNumber && (
                      <div className="error-msg mt-1">{errors.contactNumber}</div>
                    )}
                  </div>

                  <div className="mb-4">
                    <textarea className="form-control" name="message" rows="6" value={formData.message} onChange={handleInputChange} placeholder="Type your message here..." ></textarea>
                    {errors.message && (
                      <div className="error-msg mt-1">{errors.message}</div>
                    )}
                  </div>

                  <div className="text-center mb-3">
                    {typeof window !== "undefined" && (
                      <Suspense fallback={<div>Loading captcha...</div>}>
                        <ReCAPTCHA ref={recaptchaRef} sitekey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
                          onChange={(token) => setFormData((prev) => ({ ...prev, captchaToken: token })) }
                        />
                      </Suspense>
                    )}
                    {errors.captcha && <div className="error-msg mt-1">{errors.captcha}</div>}
                  </div>

                  <div className="send-msg-btn">
                    <button className="btn btn-primary" type="submit" disabled={!utilsLoaded || isSubmitting}>
                      {isSubmitting ? (
                        <>
                          Sending... <i className="fa fa-spinner fa-spin"></i>
                        </>
                      ) : utilsLoaded ? (
                        <>
                          Send Message <i className="fa fa-arrow-right"></i>
                        </>
                      ) : (
                        <>Loading phone validation...</>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
}

export default Contact;
